import { assign } from 'xstate';
import logger from '../../../logger';
import { fetchLabelsFromCloud } from '../../cloud/labels';
import { fetchTrashedNotesFromCloud } from '../../cloud/trash';
import { attachmentsMachine } from '../../machines/attachmentsMachine';
import type { AppStateContext } from '../../../types/appState';

// ---------------------------------------------------------------------------
// Idle event routing
// ---------------------------------------------------------------------------

export const miscIdleOn = {
  fetchLabels: { target: 'fetchingLabels' },
  fetchTrashedNotes: { target: 'fetchingTrashedNotes' },
};

// ---------------------------------------------------------------------------
// Top-level event handlers
// ---------------------------------------------------------------------------

export const miscTopLevelOn = {
  uploadFiles: {
    target: '.uploadingFiles',
    actions: assign<AppStateContext, any>((context, event) => {
      const { note_id, files } = event.data?.filesToAddObject ?? {};
      logger.info({ note_id, fileCount: files?.length }, 'uploadFiles: queuing upload');
      return {
        attachmentNoteId: note_id ?? null,
        pendingUploadFiles: files ?? [],
        attachmentStatus: 'uploading',
      };
    }),
  },

  /* Trash tab management */
  openTrashNote: {
    actions: assign<AppStateContext, any>((context, event) => {
      const noteId = event.data?.noteId;
      const alreadyOpen = context.openTrashNotes.includes(noteId as any);
      return {
        openTrashNotes: alreadyOpen ? context.openTrashNotes : [...context.openTrashNotes, noteId],
        selectedTrashNote: noteId,
      };
    }),
  },

  selectTrashNote: {
    actions: assign<AppStateContext, any>((context, event) => ({
      selectedTrashNote: event.data?.note?.note_id ?? null,
    })),
  },

  closeTrashNote: {
    actions: assign<AppStateContext, any>((context, event) => {
      const noteId = event.data?.noteId;
      const remaining = context.openTrashNotes.filter((id: any) => id !== noteId);
      return {
        openTrashNotes: remaining,
        selectedTrashNote:
          context.selectedTrashNote === noteId
            ? (remaining[remaining.length - 1] ?? null)
            : context.selectedTrashNote,
      };
    }),
  },

  setItemStatus: {
    actions: assign<AppStateContext, any>((context, event) => ({
      itemStatus: event.data?.status ?? 'notes',
    })),
  },
};

// ---------------------------------------------------------------------------
// State definitions
// ---------------------------------------------------------------------------

export const miscStates = {
  fetchingTrashedNotes: {
    id: 'fetchingTrashedNotes',
    invoke: {
      src: () => fetchTrashedNotesFromCloud(),
      onDone: {
        target: '#idle',
        actions: assign<AppStateContext, any>((context, event) => {
          logger.info({ count: Object.keys(event.data || {}).length }, 'fetchTrashedNotes: received trashed notes');
          return { trashedNotes: event.data ?? {}, hasTrashedNotes: true };
        }),
      },
      onError: {
        target: '#idle',
        actions: (context: any, event: any) => {
          logger.error({ error: event.data }, 'fetchTrashedNotes: failed to fetch trashed notes from cloud');
        },
      },
    },
  },

  fetchingLabels: {
    id: 'fetchingLabels',
    invoke: {
      src: () => fetchLabelsFromCloud(),
      onDone: {
        target: '#idle',
        actions: assign<AppStateContext, any>((context, event) => {
          logger.info({ count: Object.keys(event.data || {}).length }, 'fetchLabels: received labels');
          return { labels: event.data ?? {}, hasLabels: true };
        }),
      },
      onError: {
        target: '#idle',
        actions: (context: any, event: any) => {
          logger.error({ error: event.data }, 'fetchLabels: failed to fetch labels from cloud');
        },
      },
    },
  },

  uploadingFiles: {
    id: 'uploadingFiles',
    invoke: {
      id: 'attachmentsMachineInvoke',
      src: attachmentsMachine,
      data: {
        note_id: (context: AppStateContext) => context.attachmentNoteId,
        files: (context: AppStateContext) => context.pendingUploadFiles,
        uploadedNote: null as any,
        error: null as any,
      },
      onDone: {
        target: '#idle',
        actions: assign<AppStateContext, any>((context, event) => {
          const { uploadedNote, note_id } = event.data;
          logger.info({ note_id }, 'uploadingFiles: attachmentsMachine done');
          const updatedNotes = uploadedNote
            ? { ...context.notes, [note_id]: uploadedNote }
            : context.notes;
          return { notes: updatedNotes, attachmentStatus: 'done' };
        }),
      },
      onError: {
        target: '#idle',
        actions: assign<AppStateContext, any>((context, event) => {
          logger.error({ error: event.data }, 'uploadingFiles: attachmentsMachine error');
          return { attachmentStatus: 'error' };
        }),
      },
    },
  },
};
